import { createFileRoute, Link, notFound, useRouter } from "@tanstack/react-router";
import { ArrowLeft, ArrowRight, Calendar, Clock, List } from "lucide-react";
import { BrandLogo } from "@/components/BrandLogo";
import { BackToTop } from "@/components/BackToTop";
import { ArticleCTA } from "@/components/ArticleCTA";
import { SITE_URL, SITE_NAME } from "@/lib/seo";
import { getPostBySlug, getRelatedPosts, type AprendaPost } from "@/lib/aprenda";
import { ArticleSkeleton } from "@/components/skeletons";

export const Route = createFileRoute("/aprenda/$slug")({
  loader: ({ params }) => {
    const post = getPostBySlug(params.slug);
    if (!post) throw notFound();
    const related = getRelatedPosts(params.slug, 3);
    return { post, related };
  },
  head: ({ params, loaderData }) => {
    const post = loaderData?.post;
    if (!post) {
      return {
        meta: [{ title: "Artigo não encontrado — Seu Bolso Tranquilo" }],
      };
    }
    const url = `${SITE_URL}/aprenda/${params.slug}`;
    const scripts = [
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Article",
          headline: post.title,
          description: post.description,
          datePublished: post.publishedAt,
          dateModified: post.updatedAt,
          author: { "@type": "Organization", name: SITE_NAME },
          publisher: {
            "@type": "Organization",
            name: SITE_NAME,
            url: SITE_URL,
          },
          mainEntityOfPage: { "@type": "WebPage", "@id": url },
        }),
      },
      {
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: [
            { "@type": "ListItem", position: 1, name: "Início", item: SITE_URL },
            { "@type": "ListItem", position: 2, name: "Aprenda", item: `${SITE_URL}/aprenda` },
            { "@type": "ListItem", position: 3, name: post.title, item: url },
          ],
        }),
      },
    ];
    if (post.faq && post.faq.length > 0) {
      scripts.push({
        type: "application/ld+json",
        children: JSON.stringify({
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faq.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
          })),
        }),
      });
    }
    return {
      meta: [
        {
          title: `${post.title} — ${SITE_NAME}`,
          key: "title",
        },
        { name: "description", content: post.description, key: "description" },
        { name: "keywords", content: (post.keywords ?? []).join(", ") },
        { property: "og:title", content: post.title, key: "og:title" },
        { property: "og:description", content: post.description, key: "og:description" },
        { property: "og:url", content: url, key: "og:url" },
        { property: "og:type", content: "article", key: "og:type" },
        { name: "twitter:card", content: "summary_large_image", key: "twitter:card" },
        { name: "twitter:title", content: post.title, key: "twitter:title" },
        { name: "twitter:description", content: post.description, key: "twitter:description" },
        { property: "article:published_time", content: post.publishedAt },
        { property: "article:modified_time", content: post.updatedAt },

      ],
      links: [{ rel: "canonical", href: url }],
      scripts,
    };
  },
  component: ArtigoPage,
  pendingComponent: ArticleSkeleton,
  notFoundComponent: () => <NotFoundView />,
  errorComponent: ({ reset }) => <ErrorView reset={reset} />,
});

function ArtigoPage() {
  const { post, related } = Route.useLoaderData();
  return <ArticleView post={post} related={related} />;
}

const PROSE_CLS =
  "prose-article max-w-none text-foreground leading-relaxed " +
  "[&_h2]:text-2xl [&_h2]:sm:text-3xl [&_h2]:font-bold [&_h2]:mt-12 [&_h2]:mb-4 [&_h2]:tracking-tight [&_h2]:scroll-mt-24 " +
  "[&_h3]:text-xl [&_h3]:font-semibold [&_h3]:mt-8 [&_h3]:mb-3 [&_h3]:scroll-mt-24 " +
  "[&_p]:text-base [&_p]:sm:text-lg [&_p]:text-muted-foreground [&_p]:mb-5 [&_p]:leading-relaxed " +
  "[&_ul]:my-5 [&_ul]:space-y-2 [&_ul]:pl-6 [&_ul]:list-disc [&_ul_li]:text-muted-foreground [&_ul_li]:leading-relaxed " +
  "[&_ol]:my-5 [&_ol]:space-y-2 [&_ol]:pl-6 [&_ol]:list-decimal [&_ol_li]:text-muted-foreground [&_ol_li]:leading-relaxed " +
  "[&_strong]:text-foreground [&_strong]:font-semibold " +
  "[&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 hover:[&_a]:text-primary/80 " +
  "[&_blockquote]:border-l-4 [&_blockquote]:border-primary/40 [&_blockquote]:pl-5 [&_blockquote]:italic [&_blockquote]:text-muted-foreground [&_blockquote]:my-6 " +
  "[&_code]:rounded [&_code]:bg-muted [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-sm";

function ArticleView({ post, related }: { post: AprendaPost; related: AprendaPost[] }) {
  const publishedLabel = new Date(post.publishedAt).toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });

  return (
    <div className="min-h-screen bg-background text-foreground">
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-background/70 border-b border-border/40">
        <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2.5">
            <BrandLogo className="size-8" alt="Logo Seu Bolso Tranquilo" />
            <span className="font-semibold">Seu Bolso Tranquilo</span>
          </Link>
          <nav className="hidden md:flex items-center gap-8 text-sm text-muted-foreground">
            <Link to="/" className="hover:text-foreground transition-colors">Início</Link>
            <Link to="/aprenda" className="hover:text-foreground transition-colors">Aprenda</Link>
            <Link to="/contato" className="hover:text-foreground transition-colors">Contato</Link>
          </nav>
          <Link
            to="/auth"
            className="inline-flex items-center gap-2 rounded-full bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
          >
            Começar grátis
            <ArrowRight className="size-4" />
          </Link>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-12 sm:py-16">
        <nav aria-label="Breadcrumb" className="text-sm text-muted-foreground mb-8">
          <Link to="/" className="hover:text-foreground">Início</Link>
          <span className="mx-2">/</span>
          <Link to="/aprenda" className="hover:text-foreground">Aprenda</Link>
        </nav>

        <article>
          <div className="mb-4">
            <span className="inline-block rounded-full bg-primary/10 text-primary px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              {post.category}
            </span>
          </div>
          <h1 className="text-3xl sm:text-5xl font-bold tracking-tight leading-tight mb-5">
            {post.title}
          </h1>
          <p className="text-lg text-muted-foreground leading-relaxed mb-6">
            {post.description}
          </p>
          <div className="flex items-center gap-5 text-sm text-muted-foreground border-y border-border/60 py-4 mb-10">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="size-4" />
              {publishedLabel}
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Clock className="size-4" />
              {post.readingMinutes} min de leitura
            </span>
          </div>

          {post.toc.length > 2 && (
            <nav
              aria-label="Tabela de conteúdo"
              className="not-prose mb-10 rounded-2xl border border-border/60 bg-card/50 p-5 sm:p-6"
            >
              <div className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wider text-primary mb-3">
                <List className="size-4" />
                Neste artigo
              </div>
              <ol className="space-y-2 text-sm">
                {post.toc
                  .filter((t) => t.level === 2)
                  .map((item, i) => (
                    <li key={item.id} className="leading-snug">
                      <a
                        href={`#${item.id}`}
                        className="text-muted-foreground hover:text-primary transition-colors inline-flex gap-2"
                      >
                        <span className="text-primary/70 font-mono text-xs pt-0.5">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <span>{item.text}</span>
                      </a>
                    </li>
                  ))}
              </ol>
            </nav>
          )}

          <div
            className={PROSE_CLS}
            dangerouslySetInnerHTML={{ __html: post.htmlBeforeCta }}
          />

          <ArticleCTA variant="inline" />

          <div
            className={PROSE_CLS}
            dangerouslySetInnerHTML={{ __html: post.htmlAfterCta }}
          />

          {post.faq && post.faq.length > 0 && (
            <section className="mt-14" aria-label="Perguntas frequentes">
              <h2 className="text-2xl sm:text-3xl font-bold mb-6">Perguntas frequentes</h2>
              <div className="space-y-5">
                {post.faq.map((item, i) => (
                  <div key={i} className="rounded-2xl border border-border/60 bg-card p-5">
                    <h3 className="font-semibold text-foreground mb-2">{item.q}</h3>
                    <p className="text-muted-foreground leading-relaxed">{item.a}</p>
                  </div>
                ))}
              </div>
            </section>
          )}

          <div className="mt-14">
            <ArticleCTA variant="final" />
          </div>
        </article>

        {related.length > 0 && (
          <section className="mt-20 border-t border-border/60 pt-12" aria-label="Continue lendo">
            <h2 className="text-2xl font-bold mb-6">Continue lendo</h2>
            <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
              {related.map((p) => (
                <Link
                  key={p.slug}
                  to="/aprenda/$slug"
                  params={{ slug: p.slug }}
                  className="group rounded-2xl border border-border/60 bg-card p-5 hover:border-primary/40 transition-colors"
                >
                  <div className="text-xs text-primary font-semibold uppercase tracking-wider mb-2">
                    {p.category}
                  </div>
                  <h3 className="font-bold leading-snug group-hover:text-primary transition-colors mb-2">
                    {p.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">
                    {p.description}
                  </p>
                </Link>
              ))}
            </div>
          </section>
        )}
      </main>

      <BackToTop />
    </div>
  );
}

function NotFoundView() {
  const params = Route.useParams();
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <h1 className="text-3xl font-bold mb-3">Artigo não encontrado</h1>
        <p className="text-muted-foreground mb-6">
          O artigo <code className="bg-muted px-1.5 py-0.5 rounded">{params.slug}</code> não existe ou foi movido.
        </p>
        <Link
          to="/aprenda"
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          <ArrowLeft className="size-4" />
          Voltar para Aprenda
        </Link>
      </div>
    </div>
  );
}

function ErrorView({ reset }: { reset: () => void }) {
  const router = useRouter();
  return (
    <div className="min-h-screen bg-background text-foreground flex items-center justify-center px-6">
      <div className="max-w-md text-center">
        <h1 className="text-3xl font-bold mb-3">Algo deu errado</h1>
        <p className="text-muted-foreground mb-6">Não foi possível carregar o artigo.</p>
        <button
          onClick={() => {
            reset();
            router.invalidate();
          }}
          className="inline-flex items-center gap-2 rounded-full bg-primary px-5 py-2.5 text-sm font-semibold text-primary-foreground hover:bg-primary/90 transition-colors"
        >
          Tentar novamente
        </button>
      </div>
    </div>
  );
}